<script setup lang="ts">
import { ChatterStatus } from '@/api/resources/chatters/model'
import type { ChattersListRequest } from '@/api/resources/chatters/model'
import { getStatus, statuses } from '@/utils/status'
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useChattersStore } from '@/stores/chatters'
import { formatToHumanReadable } from '@/utils'
import { useAuthStore } from '@/stores/auth'
import dayjs from 'dayjs'
import { dateShortcuts } from '@/data/date-shortcuts'

const authStore = useAuthStore()

const route = useRoute()
const router = useRouter()

const store = useChattersStore()

const usernameFilter = ref('')
const statusFilter = ref<ChatterStatus | undefined>(undefined)
const inactiveFilter = ref(0)
const referralFilter = ref('')
const nudgedChattersFilter = ref(false)
const currentPage = ref(1)
const pageSize = 50

const statusChangeDateRangeFilter = ref<[Date | undefined, Date | undefined]>([
  undefined,
  undefined,
])

const toggleStatusFilter = (status: ChatterStatus) => {
  if (statusFilter.value === status) {
    statusFilter.value = undefined
  } else {
    statusFilter.value = status
  }
  currentPage.value = 1
  fetchChatters()
}

const buildParams = (): ChattersListRequest => {
  const from = statusChangeDateRangeFilter.value[0]
    ? dayjs(statusChangeDateRangeFilter.value[0]).startOf('day').toDate()
    : undefined
  const to = statusChangeDateRangeFilter.value[1]
    ? dayjs(statusChangeDateRangeFilter.value[1]).endOf('day').toDate()
    : undefined

  return {
    page: currentPage.value,
    limit: pageSize,
    from,
    to,
    status: statusFilter.value,
    username: usernameFilter.value.replace('@', '') || undefined,
    referral: referralFilter.value.replace('@', '') || undefined,
    inactiveDays: inactiveFilter.value > 0 ? inactiveFilter.value : undefined,
    nudged: nudgedChattersFilter.value || undefined,
  }
}

const fetchChatters = () => {
  store.fetch(buildParams())
}

const onFilterChange = () => {
  currentPage.value = 1
  fetchChatters()
}

const onPageChange = (page: number) => {
  currentPage.value = page
  fetchChatters()
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null
const onTextFilterInput = () => {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    currentPage.value = 1
    fetchChatters()
  }, 300)
}

const rowClick = ({ id }: { id: number }) => {
  router.push({ query: { ...route.query, chatter: id } })
}

fetchChatters()
</script>

<template>
  <main>
    <div class="filters-block status-filters" v-if="!authStore.isCurator">
      <el-tag
        size="small"
        @click="statusFilter = undefined; onFilterChange()"
        type="info"
        class="status-filter-btn"
        :class="{ 'status-filter-btn-active': !statusFilter }"
      >
        Все
        {{ store.total }}
      </el-tag>
      <el-tag
        v-for="status in Object.keys(statuses)"
        :key="status"
        size="small"
        @click="toggleStatusFilter(status as ChatterStatus)"
        :type="getStatus(status as ChatterStatus).type"
        class="status-filter-btn"
        :class="{ 'status-filter-btn-active': statusFilter === status }"
      >
        {{ getStatus(status as ChatterStatus).label }}
        {{ store.stats[status as ChatterStatus] ?? 0 }}
        ({{ store.percents[status as ChatterStatus] ?? '0%' }})
      </el-tag>
    </div>
    <div class="filters-block">
      <el-form-item label="Изменен статус" label-position="top" size="small">
        <el-date-picker
          v-model="statusChangeDateRangeFilter"
          type="daterange"
          start-placeholder="От"
          end-placeholder="До"
          @change="onFilterChange"
          :shortcuts="dateShortcuts"
        />
      </el-form-item>
      <el-space>
        <el-form-item label="Дней+ в статусе" label-position="top" size="small">
          <el-input-number
            v-model="inactiveFilter"
            :min="0"
            :precision="0"
            placeholder="Количество дней"
            @change="onFilterChange"
          />
        </el-form-item>
        <el-form-item label="Подтолкнутые" label-position="top" size="small">
          <el-switch v-model="nudgedChattersFilter" @change="onFilterChange" />
        </el-form-item>
      </el-space>
    </div>

    <div class="table-container">
      <el-table
        :data="store.chatters"
        class="chats-table"
        @row-click="rowClick"
        v-loading="store.loading"
      >
        <el-table-column>
          <template #header>
            <div style="display: flex; flex-direction: column; gap: 4px">
              <el-input
                v-model="usernameFilter"
                size="small"
                placeholder="Telegram"
                clearable
                style="width: 65px"
                @input="onTextFilterInput"
                @clear="onFilterChange"
              />
            </div>
          </template>
          <template #default="{ row }">
            {{ row.tg_peers[0].username || row.tg_peers[0].name }}
          </template>
        </el-table-column>
        <el-table-column label="Имя" prop="name" />
        <el-table-column label="Куратор" sortable sort-by="curator_id" v-if="!authStore.isCurator">
          <template #default="{ row }">
            {{ store.curators.find((curator) => curator.id === row.curator_id)?.username }}
          </template>
        </el-table-column>
        <el-table-column>
          <template #header>
            <div style="display: flex; flex-direction: column; gap: 4px">
              <el-input
                v-model="referralFilter"
                size="small"
                placeholder="Реферал"
                clearable
                style="width: 65px"
                @input="onTextFilterInput"
                @clear="onFilterChange"
              />
            </div>
          </template>
          <template #default="{ row }">
            {{ row.referrer_username }}
          </template>
        </el-table-column>
        <el-table-column label="Статус" sortable sort-by="status_changed_at">
          <template #default="{ row }">
            <el-tag :type="getStatus(row.status).type" size="small">
              {{ getStatus(row.status).label }}
            </el-tag>
            <br />
            {{ formatToHumanReadable(row.status_changed_at) }}
          </template>
        </el-table-column>
        <el-table-column label="Сообщений / Последнее" sortable sort-by="last_message_at">
          <template #default="{ row }">
            {{ row.messages_count }}
            <br />
            {{ formatToHumanReadable(row.last_message_at) }}
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="store.total > pageSize"
        class="pagination"
        layout="prev, pager, next"
        :total="store.total"
        :page-size="pageSize"
        :current-page="currentPage"
        @current-change="onPageChange"
      />
    </div>
  </main>
</template>

<style scoped>
.table-container {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.chats-table :deep(.el-table__row) {
  cursor: pointer;
}

.chats-table {
  width: 100%;
  font-size: 14px;
}
/* Мобильные стили */
@media (max-width: 768px) {
  .chats-table {
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .chats-table {
    font-size: 11px;
  }
}

@media (max-width: 360px) {
  .chats-table {
    font-size: 10px;
  }
}

.chats-table :deep(.el-tag) {
  font-size: inherit;
  padding: 2px 6px;
  height: auto;
  line-height: 1.2;
  white-space: normal;
  word-wrap: break-word;
  word-break: break-word;
  max-width: 100%;
}

.pagination {
  margin-top: 16px;
  justify-content: center;
}

.filters-block {
  padding: 1em 0.5em;
}

.status-filters {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 8px;
  align-items: center;
  justify-items: stretch;
}

.status-filter-btn {
  cursor: pointer;
  font-size: 12px;
  padding: 6px 8px;
  height: auto;
  min-height: 28px;
  width: 100%;
  text-align: center;
  white-space: nowrap;
}
.status-filter-btn-active {
  border: 1px solid #14b8a6;
  text-shadow: 0 0 1px rgb(135, 135, 135);
}

@media (min-width: 1024px) {
  .status-filters {
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 8px;
  }
}

@media (min-width: 769px) and (max-width: 1023px) {
  .status-filters {
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 6px;
  }

  .status-filter-btn {
    font-size: 11px;
    padding: 5px 6px;
    min-height: 26px;
  }
}

@media (max-width: 768px) {
  .status-filters {
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    gap: 6px;
  }

  .status-filter-btn {
    font-size: 11px;
    padding: 5px 6px;
    min-height: 26px;
  }
}

@media (max-width: 480px) {
  .chats-table :deep(.el-icon) {
    font-size: 10px;
  }

  .status-filters {
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
  }

  .status-filter-btn {
    font-size: 10px;
    padding: 4px 6px;
    min-height: 24px;
  }
}

@media (max-width: 360px) {
  .chats-table :deep(.el-icon) {
    font-size: 9px;
  }

  .status-filters {
    grid-template-columns: repeat(2, 1fr);
    gap: 4px;
  }

  .status-filter-btn {
    font-size: 9px;
    padding: 3px 4px;
    min-height: 22px;
  }
}
</style>
